import { useCallback, useContext, useEffect, useState } from "react";

import { DASH_LINKS } from "../../const/AppCardLinks";
import { formatDate } from "../DateFormatter";
import { GlobalContext } from "../../components/contextProvider/ContextProvider";
import { grades } from "../../const/Grades";
import { isWindowOpen } from "../ActionControlWindows";

/**
 * ELEMENTARY PROGRESS REPORT BUSINESS LOGIC
 * ------------------------------------------------------------------------------------------------
 * we need additional microservices from the backend
 *
 * Student must have a StudentNumber
 * Student must NOT be homeschool student (student-info/student/{$personId}/{$schoolId}/detail.json)
 * get $school = getSearchableSchools(searchString=“&icId=$student.schoolId) (user/location/searchable/index.json)[0] // TBD!!!!
 * get $archiveList = getGradingProfileArchiveForStudent (epr/gradingProfile/archive/student/{$studentNumber}/index.json)
 * if (NOT school || NOT elementary school) && empty archiveList
 *      return
 * get current $schoolYearKey from user service
 * get $studentInformation = epr/gradingProfile/student/information/$studentNumber/$schoolYearKey/detail.json
 * if $studentInformation
 *      get $timePeriodKeyToGradingProfileKeyMap = $studentInformation.timePeriodToGradingProfileKeyMap
 * if $timePeriodKeyToGradingProfileKeyMap
 *      $actionControls = epr/actioncontrols/basic/{$schoolYearKey}/index.json
 *      $defaultAllowed = true
 *      $latestTimePeriodKey = null
 *      forEach $actionControls as $actionControl
 *          if $actionControl.action === EPR_PARENT_VIEW
 *              forEach $actionControl.rangeByWhoDtoList as $range
 *                  if $range.whoType === LOCATION AND $range.who === $school
 *						$defaultAllowed = false
 *						if $range.startDate <= NOW AND $range.endDate >= NOW
 *							$latestTimePeriodKey = $range.differentiator
 *					else if $range.whoType === DEFAULT AND $defaultAllowed
 *						if $range.startDate <= NOW AND $range.endDate >= NOW
 *							$latestTimePeriodKey = $range.differentiator
 *		if NOT $latestTiePeriodKey OR NOT $timePeriodKeyToGradingProfileKeyMap[$latestTimePeriodKey]
 *			$latestTimePeriodKey = $timePeriodKeyToGradingProfileKeyMap[$timePeriodKeyToGradingProfileKeyMap.length-1]
 *		get $parentAction = $latestTimePeriodKey ? epr/actioncontrols/$employeeGuid/$schoolYearKey/EPR_PARENT_VIEW/$latestTimePeriodKey/index.json ? epr/actioncontrols/$employeeGuid/$schoolYearKey/EPR_PARENT_VIEW/index.json
 *		if $parentAction AND $parentAction.isActive
 *			$gradingProfileKey = $timePeriodKeyToGradingProfileKeyMap[$latestTimePeriodKey]
 *			get $gradingProfileDto = epr/gradingProfile/student/{$studentNumber}/{$gradingProfileKey}/detail.json
 *			if $gradingProfileDto AND $gradingProfileDto.status !== NOT_RELEASED
 *				$content = View Current EPR
 *				$showArchives = 0
 *			else
 *				$content = Your student's EPR isn't ready yet. Please contact your student's school for more information!
 *				$showArchives = 1
 *			$content += <br /><span class='highlighted-text survey-text'>&nbsp;We want your feedback -- <a class='survey-link' href='https://forms.gle/WDU8dy1kLjrfZgkJA' target='_blank'>Complete EPR Survey</a></span>
 *		else if NOT $parentAction.isActive
 *			$content += Available $parentAction.startDate
 * else
 *		if $school AND $school.isElementarySchool
 *			if $parentAction AND NOT $parentAction.isActive
 *				$latestSchoolYear = $schoolYearDto.label
 *				$content = Available $parentAction.startDate
 *				$showArchives = 1
 *			else if NOT $school.locationType === CHARTER_SCHOOL
 *				$content = Your student's EPR isn't ready yet. Please contact your student's school for more information.
 *				$showArchive = 1
 * if $archiveList AND $showArchives === 1
 *		$prevYear = max(keys($archiveList)
 *		$semKey = max($archiveList[$prevYear]
 *		$content += <a class='btn' href='/guardian/epr/student/report/{$student->getStudentNumber()}/{$previousYearSchoolKey}/{$semesterKey}'>Most Recent EPR</a>
 *		$schoolTypes = [CHARTER_SCHOOL, MIDDLE_SCHOOL, HIGH_SCHOOL]
 *		if $schoolTypes.includes($school.locationType)
 *			$content = <a class='btn' href='/guardian/epr/student/report/{$student->getStudentNumber()}/{$previousYearSchoolKey}/{$semesterKey}'>View Archived EPRs</a>
 * if $content === “”
 *		return null

    More Info sub-window will display archive
    Status: Available, Not Available
    Show dates for current time period

    Main button text is contextual - maybe view 1st semester EPR
 * ------------------------------------------------------------------------------------------------
 * Custom hook to determine if a student's guardian has access to an Elementary Progress Report
 * @name useEprApp
 * @param {{}} student
 * @returns {{}} {appObj, loading}
 */
export const useEprApp = (student) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { allLocationDtos, schoolYearDto, studentAppObjects, studentsHomeSchooled, timePeriods, token } = state || {};

    const [appObj, setAppObj] = useState(null);
    const [loading, setLoading] = useState(false);
    const [locationDto, setLocationDto] = useState(null);

    const studentKey = student?.key;
    const existing = studentAppObjects?.[studentKey]?.EPR || null;

    /**
     * @name dispatchAppObject
     * @description Dispatch the appObj to the context
     * @callback
     * @param {{}} obj - The appObj to dispatch
     */
    const dispatchAppObject = useCallback(
        (obj) => {
            dispatch({
                type: "StudentAppObjects",
                studentAppObjects: {
                    ...studentAppObjects,
                    [studentKey]: {
                        ...studentAppObjects?.[studentKey],
                        EPR: {
                            appObj: obj
                        }
                    }
                }
            });
        },
        [dispatch, studentAppObjects, studentKey]
    );

    /**
     * If we have the student's apps in context, set the appObj for return
     */
    useEffect(() => {
        if (existing) {
            setAppObj(existing.appObj);
        }
    }, [existing]);

    /**
     * Get the selected student's school location
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (!allLocationDtos || !student) {
            setAppObj(null);

            return;
        }

        const getLocationDto = () => {
            try {
                const { locationKey, schoolId } = student;
                let locMatch = [];
                if (schoolId) {
                    locMatch = allLocationDtos.reduce((result, obj) => {
                        const { icId } = obj;
                        const schoolMatch = icId.filter((item) => parseInt(item, 10) === parseInt(schoolId, 10));
                        if (schoolMatch && schoolMatch.length > 0) {
                            result.push(obj);
                        }

                        return result;
                    }, []);
                } else if (locationKey) {
                    locMatch = allLocationDtos.filter((obj) => parseInt(obj.key, 10) === parseInt(locationKey, 10));
                }
                if (locMatch && locMatch?.length > 0) {
                    setLocationDto(locMatch[0]);
                }
            } catch (error) {
                console.error(`Unable to retrieve a Location for Student ${student.studentNumber}:\n`, error);
            }
        };

        getLocationDto();
    }, [allLocationDtos, existing, student]);

    /**
     * If we have a viable location, construct the appObj
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (!locationDto || !schoolYearDto || !timePeriods || !token || !student?.studentNumber) {
            setAppObj(null);

            return;
        }

        /**
         * @name fetchEprApp
         * @description Check if the student has an EPR app
         * @async
         * @return {{} eprApp || null}
         */
        const fetchEprApp = () => {
            try {
                const parentRequest = timePeriods.filter(
                    (obj) => obj.name !== "Full Year" && isWindowOpen(obj.startDate, obj.endDate)
                );
                if (parentRequest.length === 0) {
                    setAppObj(null);
                    return null;
                }
                const buttons = [
                    {
                        // View Archived EPRs
                        label: "EPRApp_Btn2",
                        link: `${DASH_LINKS.epr_archive}/${student.studentNumber}`
                    }
                ];
                // if (["K", "01", "02", "03", "04", "05", "06"].includes(grades(student?.gradeName))) {
                //     buttons.unshift({
                //         // Go to EPR
                //         label: "EPRApp_Btn1",
                //         href: DASH_LINKS.legacy
                //     });
                // }
                if (["K", "01", "02", "03", "04", "05", "06", "07", "08"].includes(grades(student?.gradeName))) {
                    const eprApp = {
                        app: "EPR",
                        // Coming Soon. View your student's Elementary Progress Report.
                        body: "EPRApp_Body",
                        buttons,
                        endDate: formatDate(parentRequest[0].endDate, true, true), // have to get the EPR windows
                        icon: "EPR_ICON",
                        moreInfo: {},
                        startDate: formatDate(parentRequest[0].startDate, true, true),
                        status: parentRequest[0].name === "1st Semester" ? "EPRApp_1stSemester" : "EPRApp_2ndSemester",
                        statusColor: "#0669B3", // pearl blue
                        subBody: null,
                        // Elementary Progress Report
                        title: "EPRApp_Header"
                    };

                    return eprApp;
                }

                return null;
            } catch (error) {
                console.error("Unable to retrieve Elementary Progress Report App:\n", error);
            }
        };

        /**
         * @name fetchIsHomeSchooled
         * @description Check if the student is home schooled
         * @async
         * @returns {bool}
         */
        const fetchIsHomeSchooled = () => {
            try {
                const { studentNumber } = student;
                const homeSchoolMatch = studentsHomeSchooled?.find(
                    (hsObj) => parseInt(hsObj.studentNumber, 10) === parseInt(studentNumber, 10)
                );

                return homeSchoolMatch?.isHomeSchooled;
            } catch (error) {
                console.error("Unable to find Home Schooled Status:\n", error);
            }
        };

        const run = () => {
            try {
                setLoading(true);
                const isHomeSchooled = fetchIsHomeSchooled();
                const app = isHomeSchooled ? null : fetchEprApp();

                dispatchAppObject(app);
            } catch (error) {
                console.error("Unable to retrieve Home Schooled Status:\n", error);
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [
        allLocationDtos,
        dispatchAppObject,
        existing,
        locationDto,
        schoolYearDto,
        student,
        studentsHomeSchooled,
        timePeriods,
        token
    ]);

    return { appObj, loading };
};
